interface CornerPathParams {
  a: number;
  b: number;
  c: number;
  d: number;
  p: number;
  cornerRadius: number;
  arcSectionLength: number;
}

interface CornerParams {
  cornerRadius: number;
  cornerSmoothing: number;
  preserveSmoothing: boolean;
  roundingAndSmoothingBudget: number;
}

// Pre-computed constants
const DEG_TO_RAD = Math.PI / 180;
const SQRT_2 = Math.sqrt(2);

// Cache for expensive trigonometric calculations
const TRIG_CACHE = new Map<string, { sin: number; cos: number; tan: number }>();

function getTrigValues(degrees: number) {
  const key = degrees.toFixed(4);
  if (!TRIG_CACHE.has(key)) {
    const radians = degrees * DEG_TO_RAD;
    TRIG_CACHE.set(key, {
      sin: Math.sin(radians),
      cos: Math.cos(radians),
      tan: Math.tan(radians),
    });
  }
  return TRIG_CACHE.get(key)!;
}

export function getPathParamsForCorner({
  cornerRadius,
  cornerSmoothing,
  preserveSmoothing,
  roundingAndSmoothingBudget,
}: CornerParams): CornerPathParams {
  // Pre-calculate smoothing adjustment
  let adjustedCornerSmoothing = cornerSmoothing;
  let p = (1 + cornerSmoothing) * cornerRadius;

  if (!preserveSmoothing) {
    const maxCornerSmoothing = roundingAndSmoothingBudget / cornerRadius - 1;
    adjustedCornerSmoothing = Math.min(cornerSmoothing, maxCornerSmoothing);
    p = Math.min(p, roundingAndSmoothingBudget);
  }

  // Pre-calculate arc measure and angles
  const arcMeasure = 90 * (1 - adjustedCornerSmoothing);
  const halfArcMeasure = arcMeasure * 0.5;
  const angleAlpha = (90 - arcMeasure) * 0.5;
  const angleBeta = 45 * adjustedCornerSmoothing;

  // Use cached trig values
  const { sin: sinHalfArc } = getTrigValues(halfArcMeasure);
  const { tan: tanHalfAlpha } = getTrigValues(angleAlpha * 0.5);
  const { cos: cosBeta, tan: tanBeta } = getTrigValues(angleBeta);

  // Calculate derived values
  const arcSectionLength = sinHalfArc * cornerRadius * SQRT_2;
  const p3ToP4Distance = cornerRadius * tanHalfAlpha;
  const c = p3ToP4Distance * cosBeta;
  const d = c * tanBeta;

  let b = (p - arcSectionLength - c - d) / 3;
  let a = 2 * b;

  // Adjust control points if needed
  if (preserveSmoothing && p > roundingAndSmoothingBudget) {
    const p1ToP3MaxDistance = roundingAndSmoothingBudget - d - arcSectionLength - c;
    const minA = p1ToP3MaxDistance / 6;
    const maxB = p1ToP3MaxDistance - minA;

    b = Math.min(b, maxB);
    a = p1ToP3MaxDistance - b;
    p = Math.min(p, roundingAndSmoothingBudget);
  }

  return { a, b, c, d, p, arcSectionLength, cornerRadius };
}

interface SVGPathInput {
  width: number;
  height: number;
  topRightPathParams: CornerPathParams;
  bottomRightPathParams: CornerPathParams;
  bottomLeftPathParams: CornerPathParams;
  topLeftPathParams: CornerPathParams;
}

// Pre-allocate array for better performance
const PATH_PARTS = new Array(9);

export function getSVGPathFromPathParams({
  width,
  height,
  topLeftPathParams,
  topRightPathParams,
  bottomLeftPathParams,
  bottomRightPathParams,
}: SVGPathInput): string {
  // Use array join instead of template literals for better performance
  PATH_PARTS[0] = `M ${(width - topRightPathParams.p).toFixed(4)} 0`;
  PATH_PARTS[1] = drawTopRightPath(topRightPathParams);
  PATH_PARTS[2] = `L ${width.toFixed(4)} ${(height - bottomRightPathParams.p).toFixed(4)}`;
  PATH_PARTS[3] = drawBottomRightPath(bottomRightPathParams);
  PATH_PARTS[4] = `L ${bottomLeftPathParams.p.toFixed(4)} ${height.toFixed(4)}`;
  PATH_PARTS[5] = drawBottomLeftPath(bottomLeftPathParams);
  PATH_PARTS[6] = `L 0 ${topLeftPathParams.p.toFixed(4)}`;
  PATH_PARTS[7] = drawTopLeftPath(topLeftPathParams);
  PATH_PARTS[8] = 'Z';

  return PATH_PARTS.join(' ');
}

// Optimized drawing functions using direct string concatenation
function drawTopRightPath({
  cornerRadius,
  a,
  b,
  c,
  d,
  p,
  arcSectionLength,
}: CornerPathParams): string {
  if (cornerRadius) {
    const abc = a + b + c;
    return `c ${a.toFixed(4)} 0 ${(a + b).toFixed(4)} 0 ${abc.toFixed(4)} ${d.toFixed(4)} a ${cornerRadius.toFixed(4)} ${cornerRadius.toFixed(4)} 0 0 1 ${arcSectionLength.toFixed(4)} ${arcSectionLength.toFixed(4)} c ${d.toFixed(4)} ${c.toFixed(4)} ${d.toFixed(4)} ${(b + c).toFixed(4)} ${d.toFixed(4)} ${abc.toFixed(4)}`;
  }
  return `l ${p.toFixed(4)} 0`;
}

function drawBottomRightPath({
  cornerRadius,
  a,
  b,
  c,
  d,
  p,
  arcSectionLength,
}: CornerPathParams): string {
  if (cornerRadius) {
    const abc = a + b + c;
    const negArcSection = (-arcSectionLength).toFixed(4);
    const negC = (-c).toFixed(4);
    const negBC = (-(b + c)).toFixed(4);
    const negABC = (-abc).toFixed(4);
    const negD = (-d).toFixed(4);

    return `c 0 ${a.toFixed(4)} 0 ${(a + b).toFixed(4)} ${negD} ${abc.toFixed(4)} a ${cornerRadius.toFixed(4)} ${cornerRadius.toFixed(4)} 0 0 1 ${negArcSection} ${arcSectionLength.toFixed(4)} c ${negC} ${d.toFixed(4)} ${negBC} ${d.toFixed(4)} ${negABC} ${d.toFixed(4)}`;
  }
  return `l 0 ${p.toFixed(4)}`;
}

function drawBottomLeftPath({
  cornerRadius,
  a,
  b,
  c,
  d,
  p,
  arcSectionLength,
}: CornerPathParams): string {
  if (cornerRadius) {
    const abc = a + b + c;
    const negA = (-a).toFixed(4);
    const negAB = (-(a + b)).toFixed(4);
    const negABC = (-abc).toFixed(4);
    const negArcSection = (-arcSectionLength).toFixed(4);
    const negD = (-d).toFixed(4);
    const negC = (-c).toFixed(4);
    const negBC = (-(b + c)).toFixed(4);

    return `c ${negA} 0 ${negAB} 0 ${negABC} ${negD} a ${cornerRadius.toFixed(4)} ${cornerRadius.toFixed(4)} 0 0 1 ${negArcSection} ${negArcSection} c ${negD} ${negC} ${negD} ${negBC} ${negD} ${negABC}`;
  }
  return `l ${(-p).toFixed(4)} 0`;
}

function drawTopLeftPath({
  cornerRadius,
  a,
  b,
  c,
  d,
  p,
  arcSectionLength,
}: CornerPathParams): string {
  if (cornerRadius) {
    const abc = a + b + c;
    const negA = (-a).toFixed(4);
    const negAB = (-(a + b)).toFixed(4);
    const negABC = (-abc).toFixed(4);
    const negArcSection = (-arcSectionLength).toFixed(4);
    const negD = (-d).toFixed(4);

    return `c 0 ${negA} 0 ${negAB} ${d.toFixed(4)} ${negABC} a ${cornerRadius.toFixed(4)} ${cornerRadius.toFixed(4)} 0 0 1 ${arcSectionLength.toFixed(4)} ${negArcSection} c ${c.toFixed(4)} ${negD} ${(b + c).toFixed(4)} ${negD} ${abc.toFixed(4)} ${negD}`;
  }
  return `l 0 ${(-p).toFixed(4)}`;
}

// Utility to clear caches if needed
export function clearDrawCaches() {
  TRIG_CACHE.clear();
}
